import { ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@microservices/wiskey-react-components'
import { Button, Grid, SxProps, Theme } from '@mui/material'
import { CellContext, ColumnDef } from '@tanstack/react-table'

import { AddMenuPointActionDialog } from '@pages/ContextMenuCreateOrEdit'
import { AddBarChartColumnDialog } from '@pages/DiagramsCreateOrEdit/components/AddBarChartColumnDialog/AddBarChartColumnDialog'
import { AddDisplayRulesValueDialog } from '@pages/DisplayRulesCreateOrEdit/components/AddDisplayRulesValueDialog'
import { AddViewActionDialog } from '@pages/EntityCreateOrEdit'
import { AddViewColumnDialog } from '@pages/EntityCreateOrEdit/components/AddViewColumnDialog/AddViewColumnDialog'
import { AddActionDialog as AddFormActionDialog } from '@pages/FormCreateOrEdit/components/AddActionDialog'
import { AddFieldDialog } from '@pages/FormCreateOrEdit/components/AddFieldDialog'
import { AddGanttActionDialog } from '@pages/GanttChart/components/GanttCreateOrEdit/components/AddGanttActionDialog'
import { AddGanttActionTimelinesDialog } from '@pages/GanttChart/components/GanttCreateOrEdit/components/AddGanttActionTimelinesDialog'
import { AddGanttFieldDialog } from '@pages/GanttChart/components/GanttCreateOrEdit/components/AddGanttFieldDialog'
import { AddSegmentDialog } from '@pages/GanttChart/components/GanttCreateOrEdit/components/GanttConfiguration/components/AddSegmentDialog'
import { AddPointGroupDialog } from '@pages/MapCreateOrEdit'
import {
  GeneralAddActionDialog,
  ObjectXAddActionDialog,
  ObjectXAddColumnDialog,
  ObjectYAddActionDialog,
  ObjectYAddColumnDialog,
} from '@pages/TimeLineCreateOrEdit'
import { TooltipAddActionDialog } from '@pages/TooltipCreateOrEdit'
import { AddTooltipPointDialog } from '@pages/TooltipCreateOrEdit/components/AddPointDialog'

import { LoadingButton } from '@components/LoadingButton'
import { SearchInTable } from '@components/SwitchTable/SearchInTable'

import { ENTITY, MODAL_TYPE, MODEL_TYPE } from '@constants'

import { ModalType, ShareActionType } from '../../types'
import { ReactTable } from '../ui/ReactTable'

type SwitchTableProps<T> = {
  columns: ColumnDef<T>[]
  rows: T[]
  btnText?: string
  loading?: boolean
  type: ShareActionType
  page?: number
  isCrud?: boolean
  pageSize?: number
  btnDisabled?: boolean
  disablePagination?: boolean
  useDragAndDrop?: boolean
  dragHandleHeader?: string
  disableSort?: boolean
  showActionsColumn?: boolean
  showVisibilityColumn?: boolean
  onChangePage?: (event: ChangeEvent<unknown>, value: number) => void
  onEdit?: (props: CellContext<T, unknown>) => void
  onHide?: (props: CellContext<T, unknown>, checked: boolean) => void
  onDelete?: (id: string | number) => void
  onDropChange?: (rows: T[], row: T, newIndex: number) => T[]
  onShowDialog: (type: ModalType, id?: string | number, isAction?: boolean) => void
  showDialog: boolean
  showSearch?: boolean
  disabledVisibilityColumn?: boolean
  disabledDeleteActionColumn?: boolean
  disabledEditActionColumn?: boolean
  disabledCopyActionColumn?: boolean
  containerSx?: SxProps<Theme>
  updateRow?: (data: T) => void
  onCreateAllEntityFields?: (onlyRequired?: boolean) => void
  onDeleteAllEntityFields?: () => void
  createAllBtnText?: string
  deleteAllBtnText?: string
  createAllRequiredBtnText?: string
  isCreateAllLoading?: boolean
  isDeleteAllLoading?: boolean
  isCreateMandatoryLoading?: boolean
  additionalElementHeaderTable?: React.ReactNode
  rowHeight?: number
}

const getDialogType = (type: ShareActionType): MODEL_TYPE => {
  switch (type) {
    case MODEL_TYPE.VIEW:
      return MODEL_TYPE.COLUMN
    case MODEL_TYPE.FORM:
      return MODEL_TYPE.FIELD
    case MODEL_TYPE.VIEW_ACTION:
      return MODEL_TYPE.VIEW_ACTION
    case MODEL_TYPE.FORM_ACTION:
      return MODEL_TYPE.FORM_ACTION
    case MODEL_TYPE.FORM_CONFIG_LIST_CONTROL_ELEMENT_ACTION:
      return MODEL_TYPE.FORM_CONFIG_LIST_CONTROL_ELEMENT_ACTION
    case MODEL_TYPE.GANTT:
      return MODEL_TYPE.GANTT
    case MODEL_TYPE.GANTT_ACTION_RESOURCE:
      return MODEL_TYPE.GANTT_ACTION_RESOURCE
    case MODEL_TYPE.GANTT_ACTION_TIMELINES:
      return MODEL_TYPE.GANTT_ACTION_TIMELINES
    case MODEL_TYPE.GANTT_SEGMENT:
      return MODEL_TYPE.GANTT_SEGMENT
    case MODEL_TYPE.DISPLAY_RULES:
      return MODEL_TYPE.DISPLAY_RULES
    case MODEL_TYPE.TOOLTIP:
      return MODEL_TYPE.TOOLTIP
    case MODEL_TYPE.BAR_CHART_COLUMN:
      return MODEL_TYPE.BAR_CHART_COLUMN
    case MODEL_TYPE.TIMELINE_OBJECT_X_COLUMN:
      return MODEL_TYPE.TIMELINE_OBJECT_X_COLUMN
    case MODEL_TYPE.TIMELINE_OBJECT_Y_COLUMN:
      return MODEL_TYPE.TIMELINE_OBJECT_Y_COLUMN
    case MODEL_TYPE.TIMELINE_OBJECT_Y_ACTION:
      return MODEL_TYPE.TIMELINE_OBJECT_Y_ACTION
    case MODEL_TYPE.TIMELINE_OBJECT_X_ACTION:
      return MODEL_TYPE.TIMELINE_OBJECT_X_ACTION
    case MODEL_TYPE.CONTEXT_MENU_POINT_ACTION:
      return MODEL_TYPE.CONTEXT_MENU_POINT_ACTION
    case MODEL_TYPE.TIMELINE_ACTION:
      return MODEL_TYPE.TIMELINE_ACTION
    case MODEL_TYPE.TOOLTIP_ACTION:
      return MODEL_TYPE.TOOLTIP_ACTION
    case MODEL_TYPE.MAP_POINT_GROUP:
      return MODEL_TYPE.MAP_POINT_GROUP
    default:
      return MODEL_TYPE.COLUMN
  }
}

export const SwitchTable = <T extends { id: string | number }>({
  columns,
  rows,
  btnText,
  type,
  btnDisabled,
  loading,
  showDialog,
  useDragAndDrop,
  dragHandleHeader,
  disablePagination,
  disableSort,
  showActionsColumn,
  showVisibilityColumn,
  onEdit,
  onHide,
  onDropChange,
  onDelete,
  onShowDialog,
  showSearch,
  disabledVisibilityColumn,
  disabledEditActionColumn,
  disabledDeleteActionColumn,
  disabledCopyActionColumn,
  containerSx,
  updateRow,
  onCreateAllEntityFields,
  onDeleteAllEntityFields,
  createAllBtnText,
  deleteAllBtnText,
  createAllRequiredBtnText,
  isCreateAllLoading = false,
  isDeleteAllLoading = false,
  isCreateMandatoryLoading = false,
  additionalElementHeaderTable,
  rowHeight,
}: SwitchTableProps<T>) => {
  const { t } = useTranslation()
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCreateAllColumnsModal, setShowCreateAllColumnsModal] = useState(false)
  const [showDeleteAllColumnsModal, setShowDeleteAllColumnsModal] = useState(false)
  const [showCreateMandatoryColumnsModal, setShowCreateMandatoryColumnsModal] = useState(false)
  const [currentRowId, setCurrentRowId] = useState<number | string | null>()
  const [globalFilter, setGlobalFilter] = useState('')
  const dialogType: MODEL_TYPE = getDialogType(type)

  const handleClickDelete = (params?: CellContext<T, unknown>) => {
    if (params?.row.original.id && !showDeleteModal) {
      setCurrentRowId(params?.row.original.id)
      setShowDeleteModal(true)

      return
    }

    if (currentRowId) {
      onDelete?.(currentRowId)
      setCurrentRowId(null)
      setShowDeleteModal(false)

      return
    }

    onDelete?.(undefined, params?.row.original)
  }

  const isShowSearch = (type: ShareActionType | ENTITY): boolean => {
    return (
      (Object.values(ENTITY).includes(type as ENTITY) && showSearch) ||
      type === MODEL_TYPE.FORM ||
      type === MODEL_TYPE.TOOLTIP
    )
  }

  const handleCloseDeleteModal = () => setShowDeleteModal(false)

  const handleSetSearch = (pattern: string) => {
    setGlobalFilter(pattern)
  }

  const handleShowCreateAllColumnsModal = () => {
    setShowCreateAllColumnsModal(true)
  }

  const handleCloseCreateAllColumnsModal = () => {
    setShowCreateAllColumnsModal(false)
  }

  const handleClickCreateAll = () => {
    handleCloseCreateAllColumnsModal()
    onCreateAllEntityFields?.()
  }

  const handleShowDeleteAllColumnsModal = () => {
    setShowDeleteAllColumnsModal(true)
  }

  const handleCloseDeleteAllColumnsModal = () => {
    setShowDeleteAllColumnsModal(false)
  }

  const handleClickDeleteAll = () => {
    handleCloseDeleteAllColumnsModal()
    onDeleteAllEntityFields?.()
  }

  const handleShowCreateMandatoryColumnsModal = () => {
    setShowCreateMandatoryColumnsModal(true)
  }

  const handleCloseCreateMandatoryColumnsModal = () => {
    setShowCreateMandatoryColumnsModal(false)
  }

  const handleClickCreateMandatory = () => {
    handleCloseCreateMandatoryColumnsModal()
    onCreateAllEntityFields?.(true)
  }

  const renderDialog = useCallback(() => {
    switch (dialogType) {
      case MODEL_TYPE.FIELD:
        return <AddFieldDialog />
      case MODEL_TYPE.COLUMN:
        return <AddViewColumnDialog />
      case MODEL_TYPE.FORM_ACTION:
        return <AddFormActionDialog />
      case MODEL_TYPE.VIEW_ACTION:
        return <AddViewActionDialog />
      case MODEL_TYPE.GANTT:
        return <AddGanttFieldDialog />
      case MODEL_TYPE.GANTT_ACTION_RESOURCE:
        return <AddGanttActionDialog />
      case MODEL_TYPE.GANTT_ACTION_TIMELINES:
        return <AddGanttActionTimelinesDialog />
      case MODEL_TYPE.GANTT_SEGMENT:
        return <AddSegmentDialog />
      case MODEL_TYPE.DISPLAY_RULES:
        return <AddDisplayRulesValueDialog />
      case MODEL_TYPE.TOOLTIP:
        return <AddTooltipPointDialog />
      case MODEL_TYPE.BAR_CHART_COLUMN:
        return <AddBarChartColumnDialog />
      case MODEL_TYPE.TIMELINE_OBJECT_Y_COLUMN:
        return <ObjectYAddColumnDialog />
      case MODEL_TYPE.TIMELINE_OBJECT_X_COLUMN:
        return <ObjectXAddColumnDialog />
      case MODEL_TYPE.TIMELINE_OBJECT_Y_ACTION:
        return <ObjectYAddActionDialog />
      case MODEL_TYPE.TIMELINE_OBJECT_X_ACTION:
        return <ObjectXAddActionDialog />
      case MODEL_TYPE.CONTEXT_MENU_POINT_ACTION:
        return <AddMenuPointActionDialog />
      case MODEL_TYPE.TIMELINE_ACTION:
        return <GeneralAddActionDialog />
      case MODEL_TYPE.TOOLTIP_ACTION:
        return <TooltipAddActionDialog />
      case MODEL_TYPE.MAP_POINT_GROUP:
        return <AddPointGroupDialog />
    }
  }, [dialogType])

  return (
    <Grid container flexDirection='column' spacing={1}>
      <Grid
        container
        item
        alignItems='center'
        justifyContent='space-between'
        sx={{ pl: '0px !important' }}
      >
        <Grid item>
          {isShowSearch(type) && (
            <SearchInTable searchValue={globalFilter} setGlobalFilter={handleSetSearch} />
          )}
          {additionalElementHeaderTable}
        </Grid>
        <Grid item>
          {deleteAllBtnText && (
            <LoadingButton
              disabled={btnDisabled}
              loading={isDeleteAllLoading}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={handleShowDeleteAllColumnsModal}
            >
              {deleteAllBtnText}
            </LoadingButton>
          )}
          {createAllBtnText && (
            <LoadingButton
              disabled={btnDisabled}
              loading={isCreateAllLoading}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={handleShowCreateAllColumnsModal}
            >
              {createAllBtnText}
            </LoadingButton>
          )}
          {createAllRequiredBtnText && (
            <LoadingButton
              disabled={btnDisabled}
              loading={isCreateMandatoryLoading}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={handleShowCreateMandatoryColumnsModal}
            >
              {createAllRequiredBtnText}
            </LoadingButton>
          )}
          {btnText && (
            <Button
              disabled={btnDisabled}
              sx={{ ml: 2 }}
              variant='contained'
              onClick={() => onShowDialog(MODAL_TYPE.CREATE)}
            >
              {btnText}
            </Button>
          )}
        </Grid>
      </Grid>
      {showDialog && renderDialog()}
      <ConfirmModal
        actionBtnText={t('modal.delete.btn')}
        isShow={showDeleteModal}
        text={t('modal.delete.warning')}
        title={t('modal.delete.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        onClose={handleCloseDeleteModal}
        onConfirm={handleClickDelete}
      />

      <ConfirmModal
        actionBtnText={t('modal.delete.btn')}
        isShow={showDeleteAllColumnsModal}
        text={t('modal.delete.warning')}
        title={t('modal.delete.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        onClose={handleCloseDeleteAllColumnsModal}
        onConfirm={handleClickDeleteAll}
      />

      <ConfirmModal
        actionBtnText={t('modal.createAll.actionButton')}
        isShow={showCreateAllColumnsModal}
        title={t('modal.createAll.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        text={t('modal.createAll.text', {
          entityField: dialogType,
        })}
        onClose={handleCloseCreateAllColumnsModal}
        onConfirm={handleClickCreateAll}
      />

      <ConfirmModal
        actionBtnText={t('modal.createMandatory.actionButton')}
        isShow={showCreateMandatoryColumnsModal}
        title={t('modal.createMandatory.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        text={t('modal.createMandatory.text', {
          entityField: dialogType,
        })}
        onClose={handleCloseCreateMandatoryColumnsModal}
        onConfirm={handleClickCreateMandatory}
      />

      <Grid item sx={[{ pl: '0px !important' }, Boolean(useDragAndDrop) && { userSelect: 'none' }]}>
        <ReactTable
          columns={columns}
          containerSx={containerSx}
          data={rows}
          disablePagination={disablePagination}
          disableSort={disableSort}
          disabledCopyActionColumn={disabledCopyActionColumn}
          disabledDeleteActionColumn={disabledDeleteActionColumn}
          disabledEditActionColumn={disabledEditActionColumn}
          disabledVisibilityColumn={disabledVisibilityColumn}
          dragHandleHeader={dragHandleHeader}
          globalFilter={globalFilter}
          loading={loading}
          rowHeight={rowHeight}
          setGlobalFilter={setGlobalFilter}
          showActionsColumn={showActionsColumn}
          showVisibilityColumn={showVisibilityColumn}
          updateRow={updateRow}
          useDragAndDrop={useDragAndDrop}
          onDelete={handleClickDelete}
          onDropChange={onDropChange}
          onEdit={onEdit}
          onHide={onHide}
        />
      </Grid>
    </Grid>
  )
}
